import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { detectPlatform, getPlatformName } from '../platforms';
import {
  PLATFORMS,
  formatIdentityLabel,
  getIdentity,
  getOtherProviders,
  getUserProfile,
} from '../utils/userIdentities';
import { getPlatformAppUrl } from '../utils/accountLinking';
import {
  formatPhoneDisplay,
  formatPhoneInput,
  normalizePhone,
} from '../utils/phone';
import '../styles/ProfileView.css';
import NotificationBell from './NotificationBell';

/** API YYYY-MM-DD → отображение ДД.ММ.ГГГГ */
function formatBirthdayDisplay(isoDate) {
  if (!isoDate) return '';
  const match = String(isoDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return String(isoDate);
}

/**
 * ДД.ММ.ГГГГ / ДД/ММ/ГГГГ / YYYY-MM-DD → YYYY-MM-DD для API.
 * Пустая строка → null. Невалидная дата → throws.
 */
function parseBirthdayInput(raw) {
  const value = String(raw || '').trim();
  if (!value) return null;

  let year;
  let month;
  let day;

  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  const dmy = value.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (iso) {
    year = Number(iso[1]);
    month = Number(iso[2]);
    day = Number(iso[3]);
  } else if (dmy) {
    day = Number(dmy[1]);
    month = Number(dmy[2]);
    year = Number(dmy[3]);
  } else {
    throw new Error('Введите дату в формате ДД.ММ.ГГГГ');
  }

  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
    throw new Error('Некорректная дата рождения');
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    throw new Error('Некорректная дата рождения');
  }

  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function ProfileView() {
  const navigate = useNavigate();
  const { user, linkPlatform, refreshUser, isAuthenticated } = useAuth();
  const [linkingProvider, setLinkingProvider] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [birthdayDrafts, setBirthdayDrafts] = useState({});
  const [savingProgramId, setSavingProgramId] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [claimingPhone, setClaimingPhone] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const refreshProfile = () => {
      refreshUser().catch((err) => {
        console.warn('[Profile] Failed to refresh user:', err);
      });
    };

    refreshProfile();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProfile();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, refreshUser]);

  const loadMemberships = async () => {
    const data = await apiService.getLoyaltyMemberships();
    const list = Array.isArray(data) ? data : [];
    setMemberships(list);
    const drafts = {};
    list.forEach((m) => {
      drafts[m.program_id] = formatBirthdayDisplay(m.birthday);
    });
    setBirthdayDrafts(drafts);
    return list;
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setMemberships([]);
      return undefined;
    }

    let cancelled = false;
    loadMemberships()
      .then((list) => {
        if (cancelled) return;
        setMemberships(list);
      })
      .catch((err) => {
        console.warn('[Profile] Failed to load loyalty:', err);
        if (!cancelled) setMemberships([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (typeof tg?.enableVerticalSwipes !== 'function') {
      return undefined;
    }

    tg.enableVerticalSwipes();
    return () => {
      tg.disableVerticalSwipes?.();
    };
  }, []);

  const currentPlatform = useMemo(() => getPlatformName(detectPlatform()), []);
  const profile = getUserProfile(user);
  const linkedPhone = profile?.phone || memberships.find((m) => m.phone)?.phone || null;
  const legacyClaimAvailable = memberships.some((m) => m.legacy_claim_available);
  const showPhoneSync = !linkedPhone || legacyClaimAvailable;

  useEffect(() => {
    if (linkedPhone) {
      setPhoneDraft(formatPhoneDisplay(linkedPhone));
    }
  }, [linkedPhone]);

  const unlinkedProviders = useMemo(() => {
    if (!currentPlatform) {
      return Object.keys(PLATFORMS);
    }
    return getOtherProviders(currentPlatform).filter(
      (provider) => !getIdentity(user, provider),
    );
  }, [user, currentPlatform]);

  const handleLink = async (provider) => {
    setError(null);
    setMessage(null);
    setLinkingProvider(provider);

    try {
      await linkPlatform(provider);
      setMessage(
        `Откройте приложение в ${PLATFORMS[provider]?.label || provider}, чтобы завершить привязку.`
      );
    } catch (err) {
      setError(err.message || 'Не удалось начать привязку');
    } finally {
      setLinkingProvider(null);
    }
  };

  const handleRefresh = async () => {
    setError(null);
    setMessage(null);
    try {
      await refreshUser();
      await loadMemberships();
      setMessage('Данные профиля обновлены');
    } catch (err) {
      setError(err.message || 'Не удалось обновить профиль');
    }
  };

  const handleSaveBirthday = async (programId) => {
    setError(null);
    setMessage(null);
    setSavingProgramId(programId);
    try {
      const birthday = parseBirthdayInput(birthdayDrafts[programId]);
      if (!birthday) {
        throw new Error('Введите дату рождения');
      }
      const updated = await apiService.updateLoyaltyMembership(programId, {
        birthday,
      });
      setMemberships((prev) =>
        prev.map((m) => (m.program_id === programId ? { ...m, ...updated } : m)),
      );
      setBirthdayDrafts((prev) => ({
        ...prev,
        [programId]: formatBirthdayDisplay(updated.birthday),
      }));
      setMessage('Дата рождения сохранена');
    } catch (err) {
      setError(err.message || 'Не удалось сохранить дату рождения');
    } finally {
      setSavingProgramId(null);
    }
  };

  const handleClaimLoyalty = async () => {
    setError(null);
    setMessage(null);
    setClaimingPhone(true);
    try {
      const phone = normalizePhone(phoneDraft || linkedPhone || '');
      setPhoneDraft(formatPhoneDisplay(phone));
      const result = await apiService.claimLoyalty(phone);
      await refreshUser();
      await loadMemberships();
      const count = Number(result?.claimed_count || 0);
      setMessage(
        count > 0
          ? `Лояльность синхронизирована (${count})`
          : 'Номер сохранён. Данные лояльности уже были перенесены',
      );
    } catch (err) {
      setError(err.message || 'Не удалось синхронизировать лояльность');
    } finally {
      setClaimingPhone(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="header profile-header">
        <div className="profile-header-text">
          <h1>Профиль</h1>
          <p className="subtitle">Аккаунт и привязки площадок</p>
        </div>
        <div className="header-right-actions profile-header-actions">
          <NotificationBell />
          <button
            type="button"
            className="btn profile-back-btn"
            onClick={() => navigate('/')}
          >
            Назад
          </button>
        </div>
      </div>

      <div className="profile-content">
        {!isAuthenticated || !profile ? (
          <div className="profile-card">
            <p>Войдите через мини-приложение, чтобы управлять профилем.</p>
          </div>
        ) : (
          <>
            <div className="profile-card">
              <h3>{profile.name}</h3>
              <p className="profile-meta">ID: {profile.id}</p>
              {currentPlatform && (
                <p className="profile-meta">
                  Текущая площадка: {PLATFORMS[currentPlatform]?.label || currentPlatform}
                </p>
              )}
              {linkedPhone && !showPhoneSync && (
                <p className="profile-meta">Телефон: {formatPhoneDisplay(linkedPhone)}</p>
              )}
            </div>

            {showPhoneSync && (
              <div className="profile-card profile-phone-sync-card">
                <h3>Синхронизация лояльности</h3>
                <p className="profile-muted">
                  Введите номер телефона из старой программы лояльности, чтобы перенести
                  баллы и уровень.
                </p>
                <div className="profile-phone-sync-controls">
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+7 (900) 000-00-00"
                    value={phoneDraft}
                    disabled={Boolean(linkedPhone)}
                    onChange={(e) => setPhoneDraft(formatPhoneInput(e.target.value))}
                  />
                  <button
                    type="button"
                    className="btn profile-phone-sync-btn"
                    disabled={claimingPhone}
                    onClick={handleClaimLoyalty}
                  >
                    {claimingPhone ? '…' : 'Синхронизировать'}
                  </button>
                </div>
              </div>
            )}

            {memberships.length > 0 ? (
              <div className="profile-card profile-loyalty-card">
                <h3>Лояльность</h3>
                <div className="profile-loyalty-list">
                  {memberships.map((m) => (
                    <div key={m.program_id} className="profile-loyalty-item">
                      <div className="profile-loyalty-item-head">
                        <strong>{m.program_name}</strong>
                        <span className="profile-loyalty-level">Ур. {m.current_level}</span>
                      </div>
                      <p className="profile-meta">
                        Потрачено: {Number(m.spent_total || 0).toFixed(0)} ₽
                        {m.amount_to_next_level != null && m.next_level
                          ? ` · до ур. ${m.next_level}: ${Number(m.amount_to_next_level).toFixed(0)} ₽`
                          : ''}
                      </p>
                      {m.enable_bonus_points && (
                        <p className="profile-meta">
                          Баллы: {Number(m.bonus_balance || 0).toFixed(0)}
                          {Number(m.bonus_credit_percent) > 0
                            ? ` · кэшбек ${Number(m.bonus_credit_percent).toFixed(0)}%`
                            : ''}
                        </p>
                      )}
                      {m.enable_level_discount && Number(m.level_discount_percent) > 0 && (
                        <p className="profile-meta">
                          Скидка уровня: {Number(m.level_discount_percent).toFixed(0)}%
                        </p>
                      )}
                      {m.birthday_active && (
                        <p className="profile-loyalty-bday">Скидка на ДР сейчас активна</p>
                      )}
                      {m.enable_birthday_discount && (
                        <div className="profile-birthday-row">
                          <label htmlFor={m.birthday ? undefined : `bday-${m.program_id}`}>
                            День рождения
                          </label>
                          {m.birthday ? (
                            <div className="profile-birthday-locked">
                              <span className="profile-birthday-value">
                                {formatBirthdayDisplay(m.birthday)}
                              </span>
                              <p className="profile-muted">
                                Чтобы изменить дату, обратитесь в поддержку
                              </p>
                            </div>
                          ) : (
                            <div className="profile-birthday-controls">
                              <input
                                id={`bday-${m.program_id}`}
                                type="text"
                                inputMode="numeric"
                                autoComplete="bday"
                                placeholder="ДД.ММ.ГГГГ"
                                maxLength={10}
                                value={birthdayDrafts[m.program_id] || ''}
                                onChange={(e) => {
                                  const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
                                  let next = digits;
                                  if (digits.length > 4) {
                                    next = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
                                  } else if (digits.length > 2) {
                                    next = `${digits.slice(0, 2)}.${digits.slice(2)}`;
                                  }
                                  setBirthdayDrafts((prev) => ({
                                    ...prev,
                                    [m.program_id]: next,
                                  }));
                                }}
                              />
                              <button
                                type="button"
                                className="btn profile-birthday-save"
                                disabled={savingProgramId === m.program_id}
                                onClick={() => handleSaveBirthday(m.program_id)}
                              >
                                {savingProgramId === m.program_id ? '…' : 'Сохранить'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="profile-card profile-loyalty-card">
                <h3>Лояльность</h3>
                <p className="profile-muted">
                  Пока нет доступных программ. Когда кофейня подключит лояльность и привяжет её
                  к точке, она появится здесь.
                </p>
              </div>
            )}

            <div className="profile-card">
              <h3>Привязанные площадки</h3>
              <ul className="profile-identity-list">
                {Object.keys(PLATFORMS).map((provider) => {
                  const identity = getIdentity(user, provider);
                  const isLinked = Boolean(identity);
                  return (
                    <li key={provider}>
                      <span className="profile-identity-badge">
                        {PLATFORMS[provider]?.label || provider}
                        {isLinked ? ' ✓' : ''}
                      </span>
                      <span>
                        {isLinked
                          ? formatIdentityLabel(identity)
                          : 'Не привязано'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {unlinkedProviders.length > 0 && (
              <div className="profile-card">
                <h3>Привязать другую площадку</h3>
                <p className="profile-muted">
                  Чтобы объединить заказы и историю, откройте приложение на другой площадке.
                  Текущая сессия сохранится, и аккаунты будут связаны автоматически.
                </p>

                <div className="profile-link-actions">
                  {unlinkedProviders.map((provider) => {
                    const platformUrl = getPlatformAppUrl(provider);
                    const isConfigured = Boolean(platformUrl);
                    return (
                      <button
                        key={provider}
                        type="button"
                        className="btn profile-link-btn"
                        disabled={!isConfigured || linkingProvider === provider}
                        onClick={() => handleLink(provider)}
                      >
                        {linkingProvider === provider
                          ? 'Открываем...'
                          : `Привязать ${PLATFORMS[provider]?.label || provider}`}
                      </button>
                    );
                  })}
                </div>

                {unlinkedProviders.some((provider) => !getPlatformAppUrl(provider)) && (
                  <p className="profile-warning">
                    Для некоторых площадок не задан URL мини-приложения в настройках.
                  </p>
                )}
              </div>
            )}

            <button type="button" className="btn btn-secondary profile-refresh-btn" onClick={handleRefresh}>
              Обновить
            </button>
          </>
        )}

        {message && <div className="profile-message profile-message-success">{message}</div>}
        {error && <div className="profile-message profile-message-error">{error}</div>}
      </div>
    </div>
  );
}

export default ProfileView;
